import React, { FC, useEffect, useRef } from 'react';
import { useFeatureFlag } from 'src/shared/feature-flags';
import { backofficeUrl, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled, { keyframes } from 'styled-components';
import { Link } from '../../../../widgets/buttons';
import { NotificationBanner } from '../../../../widgets/notificationBanner';
import { ZTooltip } from '../../../../widgets/ztooltip';
import { Model3DParameterValues } from '../../modelGeneration3DParameters';
import { AdOptions, InputImageData, Model3DModelSelections, ModelGeneration3DModel, ToolType, VideoOptions } from '../../types';
import { PresetDropdownValue } from '../presets/presetDropdown';
import { AI_STUDIO_HELP_URL } from '../shared/aiStudioHelpLink';
import { CreativeToolLandingView } from './creativeToolLandingView';
import { Model3DLandingView } from './model3dLandingView';

import adGeneratorImg from 'assets/images/aistudio/ad_generator_new.png';
import image3dPreviewImg from 'assets/images/aistudio/image3d_preview.png';
import multiFusionImg from 'assets/images/aistudio/multi_fusion_new.png';
import multiview3dPreviewImg from 'assets/images/aistudio/multiview3d_preview.png';
import productStagingImg from 'assets/images/aistudio/product_staging_new.png';
import magicSwapImg from 'assets/images/aistudio/product_transformer_new.png';
import text3dPreviewImg from 'assets/images/aistudio/text3d_preview.png';
import tryonImg from 'assets/images/aistudio/tryon_new.png';
import upscalingImg from 'assets/images/aistudio/upscaler_new.png';
import videoGeneratorImg from 'assets/images/aistudio/video_generator.png';

/* ─── Tool data ───────────────────────────────────────────────────────────── */

type HomepageToolCardDefinition = {
    id: ToolType;
    name: string;
    description: string;
    thumbnail: string;
};

export const CREATIVE_STUDIO_TOOL_CARDS: HomepageToolCardDefinition[] = [
    {
        id: 'virtual-tryon',
        name: 'Virtual Model',
        description: 'Generate photorealistic mockups with products worn by AI models in any pose or environment.',
        thumbnail: tryonImg
    },
    {
        id: 'product-staging',
        name: 'Product Staging',
        description: 'Place products in realistic scenes or clean studio shots with full control over context.',
        thumbnail: productStagingImg
    },
    {
        id: 'magic-swap',
        name: 'Product Transformer',
        description: 'Change color, material, or shape of any product for photorealistic or stylized visuals.',
        thumbnail: magicSwapImg
    },
    {
        id: 'multi-image-fusion',
        name: 'Multi-Product Scene',
        description: 'Combine multiple products into a single scene with full control over environment and lighting.',
        thumbnail: multiFusionImg
    },
    {
        id: 'ad-generator',
        name: 'Ad Builder',
        description: 'Turn product images into ready-to-publish ad creatives in seconds.',
        thumbnail: adGeneratorImg
    },
    {
        id: 'video-generation',
        name: 'Video',
        description: 'Transform a single image into a dynamic video with motion, context, and effects.',
        thumbnail: videoGeneratorImg
    },
    {
        id: 'upscaler',
        name: 'Upscale',
        description: 'Enhance product images up to 4x resolution with AI-powered detail and clarity.',
        thumbnail: upscalingImg
    }
];

export const MODEL3D_SUBCARDS: HomepageToolCardDefinition[] = [
    {
        id: 'text-to-3d',
        name: 'Text to 3D',
        description: 'Type a prompt and watch our AI sculpt a high-quality 3D asset. No modeling skills needed.',
        thumbnail: text3dPreviewImg
    },
    {
        id: 'image-to-3d',
        name: 'Image to 3D',
        description: 'Upload a photo or sketch and watch the AI create a high-fidelity, textured 3D model.',
        thumbnail: image3dPreviewImg
    },
    {
        id: 'multi-views-to-3d',
        name: 'Multi-View to 3D',
        description: 'Build accurate 3D models from 1–8 angles with AI-powered geometry and depth capture.',
        thumbnail: multiview3dPreviewImg
    }
];

const HOMEPAGE_TOOL_CARDS = [...CREATIVE_STUDIO_TOOL_CARDS, ...MODEL3D_SUBCARDS];

const createHomepageToolMap = <Value,>(getValue: (card: HomepageToolCardDefinition) => Value) =>
    HOMEPAGE_TOOL_CARDS.reduce<Partial<Record<ToolType, Value>>>((map, card) => {
        map[card.id] = getValue(card);
        return map;
    }, {});

const HOMEPAGE_TOOL_NAMES: Partial<Record<ToolType, string>> = createHomepageToolMap((card) => card.name);

const HOMEPAGE_TOOL_DESCRIPTIONS: Partial<Record<ToolType, string>> = createHomepageToolMap((card) => card.description);

/* ─── Animations ──────────────────────────────────────────────────────────── */

const gradientShift = keyframes`
    0%   { background-position: 0% center; }
    50%  { background-position: 100% center; }
    100% { background-position: 0% center; }
`;

/* ─── Page layout ─────────────────────────────────────────────────────────── */

const ContentWrapper = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 24px 40px 36px;
    font-family: 'Inter', sans-serif;
    position: relative;
`;

const IntroSection = styled.div<{ $hidden?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 28px;
    opacity: ${(p) => (p.$hidden ? 0 : 1)};
    pointer-events: ${(p) => (p.$hidden ? 'none' : 'auto')};
    transition: opacity 0.3s ease;
`;

const IntroTitle = styled.h1`
    font-size: 38px;
    font-weight: 700;
    color: #1e0a3c;
    margin: 0 0 10px;
    line-height: 1.2;
`;

const IntroTitleAccent = styled.span`
    background: linear-gradient(90deg, #7c3aed, #a855f7, #ec4899, #a855f7, #7c3aed);
    background-size: 300% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientShift} 8s ease infinite;
`;

const IntroSubtitle = styled.p`
    font-size: 15px;
    font-weight: 400;
    color: rgba(30, 10, 60, 0.58);
    margin: 0 0 8px;
    width: 100%;
    max-width: 1300px;
    text-align: center;
    line-height: 1.7;
`;

const LearnMoreLink = styled.a`
    font-size: 13px;
    color: #3b82f6;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

/* ─── Credits banner ──────────────────────────────────────────────────────── */

const BannerInfoTool = styled(NotificationBanner)`
    margin-top: 15px;
    margin-bottom: 20px;
`;

const ExtraBannerContent = styled.span`
    display: inline-block;
    margin: auto;
    font-weight: bold;

    > a {
        color: #0288d1;
        text-decoration: underline;
    }
`;

/* ─── Cards area — position:relative so overlay anchors to it ─────────────── */

const CardsAreaWrapper = styled.div`
    position: relative;
    max-width: 1300px;
    margin: 0 auto;
    width: 100%;
`;

/* Overlay expands to the full page below the Header (Header min-height: 64px) */
const CardsOverlay = styled.div`
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

/* ─── Sections ────────────────────────────────────────────────────────────── */

const SectionsWrapper = styled.div<{ $hidden?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 28px;
    opacity: ${(p) => (p.$hidden ? 0 : 1)};
    pointer-events: ${(p) => (p.$hidden ? 'none' : 'auto')};
    transition: opacity 0.3s ease;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const SectionTitle = styled.h2`
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: rgba(30, 10, 60, 0.38);
    margin: 0;
    white-space: nowrap;
`;

const SectionDivider = styled.div`
    flex: 1;
    height: 1px;
    background: #e5e7eb;
`;

/* ─── Card grid ───────────────────────────────────────────────────────────── */

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 126px;
    gap: 14px;

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 440px) {
        grid-template-columns: 1fr;
    }
`;

/* ─── Card ────────────────────────────────────────────────────────────────── */

const ToolCard = styled.div<{ $disabled?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 16px 14px;
    height: 100%;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    opacity: ${(props) => (props.$disabled ? 0.45 : 1)};
    filter: ${(props) => (props.$disabled ? 'grayscale(1)' : 'none')};
    overflow: hidden;
    box-sizing: border-box;
    transition:
        background 0.2s ease,
        border-color 0.2s ease,
        box-shadow 0.2s ease,
        transform 0.2s ease;

    &:hover {
        background: ${(props) => (props.$disabled ? '#ffffff' : '#fdf8ff')};
        border-color: ${(props) => (props.$disabled ? '#e5e7eb' : '#a855f7')};
        box-shadow: ${(props) => (props.$disabled ? 'none' : '0 4px 24px rgba(168, 85, 247, 0.18)')};
        transform: ${(props) => (props.$disabled ? 'none' : 'translateY(-2px)')};
    }
`;

const CardImageWrapper = styled.div`
    width: 80px;
    height: 80px;
    flex-shrink: 0;
    border-radius: 8px;
    overflow: hidden;
`;

const CardPreviewImage = styled.img`
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: center;
`;

const CardTextBlock = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 3px;
    min-width: 0;
    overflow: hidden;
`;

const CardTitle = styled.h3`
    font-size: 15px;
    font-weight: 700;
    margin: 0;
    color: ${(props) => props.theme.app.textColor};
    line-height: 1.25;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const CardDesc = styled.p`
    font-size: 13px;
    color: #6b7280;
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

/* ─── Component ───────────────────────────────────────────────────────────── */

interface AIStudioHomepageProps {
    onToolSelect: (toolId: ToolType) => void;
    onModel3DAgentSelect?: (
        agentId: string,
        prompt?: string,
        imageFile?: File,
        multiViewReady?: boolean,
        imageUrl?: string,
        mvFiles?: Array<{ file: File; viewType: string }>
    ) => void;
    onOpenCatalog?: (toolId: ToolType) => void;
    onGenerateFromLanding?: (toolId: ToolType, images: InputImageData[], prompt: string, selections: Record<string, PresetDropdownValue | null>) => void;
    onSelectFromLibraryForLanding?: (onSelect: (imageUrl: string) => void) => void;
    overlayToolId: ToolType | null;
    onOverlayToolChange: (id: ToolType | null) => void;
    overlayAgentId: string | null;
    onOverlayAgentChange: (id: string | null) => void;
    videoOptions?: VideoOptions | null;
    onVideoOptionsChange?: (options: VideoOptions) => void;
    adOptions?: AdOptions;
    onAdOptionsChange?: (options: AdOptions) => void;
    modelGeneration3DModels?: readonly ModelGeneration3DModel[];
    model3DModelSelections?: Model3DModelSelections;
    onModel3DModelChange?: (agentId: string, model: string) => void;
    getModel3DParameterValues?: (agentId: string | null | undefined) => Model3DParameterValues | undefined;
    onModel3DParameterValuesChange?: (agentId: string, model: string, values: Model3DParameterValues) => void;
    disabledTooltipByToolId?: Record<string, string | undefined>;
    /** Product image URL from the catalog/iframe flow — propagated to Model3DLandingView so the
     *  3D agent landings can pre-fill their inputs (e.g. multi-views-to-3d front slot). */
    productImageUrl?: string;
}

export const AIStudioHomepage: FC<AIStudioHomepageProps> = ({
    onModel3DAgentSelect,
    onOpenCatalog,
    onGenerateFromLanding,
    onSelectFromLibraryForLanding,
    overlayToolId,
    onOverlayToolChange,
    overlayAgentId,
    onOverlayAgentChange,
    videoOptions,
    onVideoOptionsChange,
    adOptions,
    onAdOptionsChange,
    modelGeneration3DModels = [],
    model3DModelSelections,
    onModel3DModelChange,
    getModel3DParameterValues,
    onModel3DParameterValuesChange,
    disabledTooltipByToolId,
    productImageUrl
}) => {
    const isAiStudio3DEnabled = useFeatureFlag('aiStudio3D');
    const { ecommerceName } = useSubscriptionData();
    const contentWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if ((overlayToolId !== null || overlayAgentId !== null) && contentWrapperRef.current) {
            contentWrapperRef.current.scrollTop = 0;
        }
    }, [overlayToolId, overlayAgentId]);

    const renderToolCard = (card: HomepageToolCardDefinition, onSelect: (toolId: ToolType) => void) => {
        const disabledTooltip = disabledTooltipByToolId?.[card.id];

        const toolCard = (
            <ToolCard key={card.id} $disabled={!!disabledTooltip} onClick={disabledTooltip ? undefined : () => onSelect(card.id)}>
                <CardImageWrapper>
                    <CardPreviewImage src={card.thumbnail} alt={card.name} />
                </CardImageWrapper>
                <CardTextBlock>
                    <CardTitle>{T._(card.name, 'Admin')}</CardTitle>
                    <CardDesc>{T._(card.description, 'Admin')}</CardDesc>
                </CardTextBlock>
            </ToolCard>
        );

        return disabledTooltip ? (
            <ZTooltip key={card.id} title={disabledTooltip} position='top' addContainer>
                {toolCard}
            </ZTooltip>
        ) : (
            toolCard
        );
    };

    return (
        <ContentWrapper ref={contentWrapperRef}>
            <IntroSection $hidden={!!overlayToolId || !!overlayAgentId}>
                <IntroTitle>
                    <IntroTitleAccent>{T._('A team of AI agents', 'Admin')}</IntroTitleAccent> {T._('working for you', 'Admin')}
                </IntroTitle>
                <IntroSubtitle>
                    {T._(
                        'Bring your products to life with AI-powered tools. Create photorealistic mockups, professional videos, and high-quality 3D assets in no time.',
                        'Admin'
                    )}
                </IntroSubtitle>
                <LearnMoreLink href='https://zakeke.zendesk.com/hc/en-us/articles/25239933114908' target='_blank' rel='noopener noreferrer'>
                    {T._('Learn more', 'Admin')}
                </LearnMoreLink>
            </IntroSection>

            <CardsAreaWrapper>
                <SectionsWrapper $hidden={!!overlayToolId || !!overlayAgentId}>
                    <Section>
                        <SectionHeader>
                            <SectionTitle>{T._('Creative Studio', 'Admin')}</SectionTitle>
                            <SectionDivider />
                        </SectionHeader>
                        <CardsGrid>{CREATIVE_STUDIO_TOOL_CARDS.map((card) => renderToolCard(card, onOverlayToolChange))}</CardsGrid>
                    </Section>

                    {isAiStudio3DEnabled && (
                        <Section>
                            <SectionHeader>
                                <SectionTitle>{T._('3D Engines', 'Admin')}</SectionTitle>
                                <SectionDivider />
                            </SectionHeader>
                            <CardsGrid>{MODEL3D_SUBCARDS.map((card) => renderToolCard(card, (id) => onOverlayAgentChange(id)))}</CardsGrid>
                        </Section>
                    )}

                    <BannerInfoTool
                        type={'EXTRA'}
                        content={
                            ecommerceName === 'SHOPIFY' ? (
                                <>
                                    {T._('This tool is billed via credits ', 'Admin')}
                                    {'('}
                                    <Link href={AI_STUDIO_HELP_URL} target={'_blank'}>
                                        {T._('see here', 'Admin')}
                                    </Link>
                                    {')'}
                                    {'. '}
                                    {T._(
                                        'When you activate the tool, a set of free credits is added to your account. Once the free credits are used up, you will be notified by email and in your Zakeke admin. To continue using the tool without interruption, you can purchase a new credit pack anytime from ',
                                        'Admin'
                                    )}
                                    <ExtraBannerContent>
                                        {'My Account > Addons > '}
                                        <Link href={backofficeUrl('/User/?tab=subscriptions#addons-title')} target={'_blank'}>
                                            {'AI Agents Studio'}
                                        </Link>
                                    </ExtraBannerContent>
                                </>
                            ) : (
                                <>
                                    {T._(
                                        'This tool is billed via credits. When you activate the tool, a set of free credits is added to your account. Once the free credits are used up, the first paid credit pack will be automatically charged',
                                        'Admin'
                                    )}
                                    {' ('}
                                    {T._('see available packs', 'Admin')}{' '}
                                    <Link href={AI_STUDIO_HELP_URL} target={'_blank'}>
                                        {T._('here', 'Admin')}
                                    </Link>
                                    {'), '}
                                    {T._('unless you disable auto-refill. You can manage your credit packs anytime from', 'Admin')}
                                    <ExtraBannerContent>
                                        {'My Account > Addons > '}
                                        <Link href={backofficeUrl('/User/?tab=subscriptions#addons-title')} target={'_blank'}>
                                            {'AI Agents Studio'}
                                        </Link>
                                    </ExtraBannerContent>
                                </>
                            )
                        }
                    />
                </SectionsWrapper>
            </CardsAreaWrapper>

            {overlayAgentId !== null && (
                <CardsOverlay>
                    <Model3DLandingView
                        key={overlayAgentId}
                        initialAgentId={overlayAgentId}
                        productImageUrl={productImageUrl}
                        modelGeneration3DModels={modelGeneration3DModels}
                        model3DModelSelections={model3DModelSelections}
                        onModel3DModelChange={onModel3DModelChange}
                        getModel3DParameterValues={getModel3DParameterValues}
                        onModel3DParameterValuesChange={onModel3DParameterValuesChange}
                        onStartCreating={(agentId, prompt, imageFile, imageUrl, multiViewReady, mvFiles) => {
                            onOverlayAgentChange(null);
                            onModel3DAgentSelect?.(agentId, prompt, imageFile, multiViewReady, imageUrl, mvFiles);
                        }}
                        onOpenCatalog={() => {
                            onOverlayAgentChange(null);
                            onOpenCatalog?.('image-to-3d');
                        }}
                        onSelectFromLibrary={(onSelect) => {
                            onSelectFromLibraryForLanding?.(onSelect);
                        }}
                    />
                </CardsOverlay>
            )}

            {overlayToolId !== null && (
                <CardsOverlay>
                    <CreativeToolLandingView
                        key={overlayToolId}
                        toolId={overlayToolId}
                        name={HOMEPAGE_TOOL_NAMES[overlayToolId] || overlayToolId}
                        description={HOMEPAGE_TOOL_DESCRIPTIONS[overlayToolId] || ''}
                        onGenerate={(images, prompt, selections) => {
                            const id = overlayToolId;
                            onOverlayToolChange(null);
                            onGenerateFromLanding?.(id, images, prompt, selections);
                        }}
                        onOpenCatalog={() => {
                            const id = overlayToolId;
                            onOverlayToolChange(null);
                            onOpenCatalog?.(id);
                        }}
                        onSelectFromLibrary={(onSelect) => {
                            onSelectFromLibraryForLanding?.(onSelect);
                        }}
                        videoOptions={videoOptions}
                        onVideoOptionsChange={onVideoOptionsChange}
                        adOptions={adOptions}
                        onAdOptionsChange={onAdOptionsChange}
                    />
                </CardsOverlay>
            )}
        </ContentWrapper>
    );
};

export default AIStudioHomepage;
